import { jsPDF } from "jspdf";
import { PAGE_LAYOUT, IMAGES } from "./Constants";
import { PDFFooter } from "./Sections/PDFFooter";
import { PDFWatermark } from "./Sections/PDFWatermark";
import { PDFGuidelines } from "./Sections/PDFGuidelines";

const COLORS = {
  PRIMARY: [255, 100, 20],
  SECONDARY: [50, 50, 50],
  SUCCESS: [0, 153, 51],
  INFO: [0, 121, 191],
  WARNING: [255, 140, 0],
  DANGER: [220, 0, 0],
  LIGHT_GRAY: [248, 248, 250],
  BORDER: [230, 230, 235],
  WHITE: [255, 255, 255],
  TEXT_DARK: [35, 35, 40],
  TEXT_LIGHT: [110, 110, 120],
};

const FONTS = {
  HEADING: { family: "helvetica", style: "bold", size: 18 },
  SUBHEADING: { family: "helvetica", style: "bold", size: 14 },
  BODY: { family: "helvetica", style: "normal", size: 10 },
  SMALL: { family: "helvetica", style: "normal", size: 8 },
};

const SPACING = {
  PAGE_MARGIN: 20,
  SECTION_GAP: 12,
  CARD_PADDING: 10,
  LINE_HEIGHT: 5,
  SMALL_GAP: 6,
};

/** Canonical order for meal types in the PDF (breakfast before dinner, etc.) */
const MEAL_TYPE_ORDER = [
  "PRE-BREAKFAST",
  "BREAKFAST",
  "MID-MORNING SNACK",
  "LUNCH",
  "EVENING SNACK",
  "SNACKS",
  "DINNER",
  "BEDTIME",
  "POST-WORKOUT",
  "PRE-WORKOUT",
];

function sortMealTypesForPDF(mealTypeKeys) {
  return [...(mealTypeKeys || [])].sort((a, b) => {
    const keyA = (a || "").toUpperCase().trim();
    const keyB = (b || "").toUpperCase().trim();
    const indexA = MEAL_TYPE_ORDER.indexOf(keyA);
    const indexB = MEAL_TYPE_ORDER.indexOf(keyB);
    if (indexA === -1 && indexB === -1) return (a || "").localeCompare(b || "");
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;
    return indexA - indexB;
  });
}

export async function GeneratePDF(
  userData,
  planData,
  get,
  empName,
  empMail,
  empId,
  nutritionGoals = {},
  options = {}
) {
  if (!userData || !planData) throw new Error("Invalid user or plan data");

  try {
    const doc = new jsPDF({ orientation: "portrait", unit: "mm" });

    // Page 1: Cover Page
    await PDFWatermark.add(doc);
    addCoverPage(doc, userData, empName, empMail);

    // Page 2: User Profile & Nutrition Summary
    doc.addPage();
    await PDFWatermark.add(doc);
    await addProfilePage(
      doc,
      userData,
      planData,
      empName,
      empMail,
      nutritionGoals
    );

    // Page 3+: All Meal Plans
    doc.addPage();
    await PDFWatermark.add(doc);
    addAllMealPlans(doc, planData, userData);

    // Final Page: Guidelines
    doc.addPage();
    await PDFWatermark.add(doc);
    PDFGuidelines.add(doc);

    // Add footers to all pages
    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      await PDFFooter.add(doc, {
        name: `${userData.firstName} ${userData.lastName}`,
        coachName: empName,
      });
    }

    const sanitizedName = `${userData.firstName}${userData.lastName}`
      .replace(/[^a-z0-9]/gi, "-")
      .toLowerCase();
    const planSlug = options.planTitle
      ? "-" + String(options.planTitle).replace(/[^a-z0-9]/gi, "-").toLowerCase().slice(0, 40)
      : "";
    const filename = `flexcellence-diet-plan-${sanitizedName}${planSlug}.pdf`;

    if (options.returnBlob) {
      const blob = doc.output("blob");
      return { blob, filename };
    }
    doc.save(filename);
  } catch (error) {
    console.error("PDF Generation Error:", error);
    throw new Error("Failed to generate PDF: " + error.message);
  }
}

// ==================== COVER PAGE ====================
function addCoverPage(doc, userData, coachName, coachEmail) {
  let y = 50;

  // Logo or Brand Header
  try {
    if (IMAGES && IMAGES.LOGO) {
      const logoSize = 45;
      const logoX = PAGE_LAYOUT.WIDTH / 2 - logoSize / 2;
      doc.addImage(IMAGES.LOGO, "PNG", logoX, y, logoSize, logoSize);
      y += logoSize + 20;
    } else {
      throw new Error("No logo");
    }
  } catch (e) {
    doc.setFillColor(...COLORS.PRIMARY);
    doc.roundedRect(35, y, 140, 38, 3, 3, "F");

    doc.setTextColor(...COLORS.WHITE);
    doc.setFont(FONTS.HEADING.family, FONTS.HEADING.style);
    doc.setFontSize(26);
    doc.text("FLEXCELLENCE", PAGE_LAYOUT.WIDTH / 2, y + 16, {
      align: "center",
    });

    doc.setFontSize(12);
    doc.setFont(FONTS.BODY.family, "italic");
    doc.text(
      "Your Personalized Nutrition Plan",
      PAGE_LAYOUT.WIDTH / 2,
      y + 28,
      {
        align: "center",
      }
    );

    y += 55;
  }

  // Client Name
  doc.setTextColor(...COLORS.TEXT_DARK);
  doc.setFont(FONTS.HEADING.family, FONTS.HEADING.style);
  doc.setFontSize(24);
  doc.text(
    `${userData.firstName} ${userData.lastName}`,
    PAGE_LAYOUT.WIDTH / 2,
    y,
    { align: "center" }
  );

  y += 12;

  // Date
  doc.setFont(FONTS.BODY.family, FONTS.BODY.style);
  doc.setFontSize(10);
  doc.setTextColor(...COLORS.TEXT_LIGHT);
  const today = new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  doc.text(`Plan Generated: ${today}`, PAGE_LAYOUT.WIDTH / 2, y, {
    align: "center",
  });

  y += 35;

  // Welcome Box
  doc.setFillColor(...COLORS.LIGHT_GRAY);
  doc.roundedRect(25, y, 160, 48, 2, 2, "F");

  doc.setFont(FONTS.SUBHEADING.family, FONTS.SUBHEADING.style);
  doc.setFontSize(13);
  doc.setTextColor(...COLORS.PRIMARY);
  doc.text("Welcome to Your Fitness Journey!", PAGE_LAYOUT.WIDTH / 2, y + 12, {
    align: "center",
  });

  doc.setFont(FONTS.BODY.family, FONTS.BODY.style);
  doc.setFontSize(10);
  doc.setTextColor(...COLORS.TEXT_DARK);
  const welcomeText = doc.splitTextToSize(
    "This personalized meal plan has been carefully crafted to help you achieve your health and fitness goals. Follow the guidelines and stay consistent for the best results.",
    150
  );
  welcomeText.forEach((line, i) => {
    doc.text(line, PAGE_LAYOUT.WIDTH / 2, y + 24 + i * 5.5, {
      align: "center",
    });
  });

  y += 65;

  // Coach Info Box
  doc.setFillColor(...COLORS.PRIMARY);
  doc.roundedRect(45, y, 120, 28, 2, 2, "F");

  doc.setTextColor(...COLORS.WHITE);
  doc.setFont(FONTS.BODY.family, FONTS.BODY.style);
  doc.setFontSize(10);
  doc.text("Your Coach", PAGE_LAYOUT.WIDTH / 2, y + 9, { align: "center" });

  doc.setFont(FONTS.SUBHEADING.family, FONTS.SUBHEADING.style);
  doc.setFontSize(13);
  doc.text(coachName || "Flexcellence Team", PAGE_LAYOUT.WIDTH / 2, y + 16.5, {
    align: "center",
  });

  doc.setFont(FONTS.SMALL.family, FONTS.SMALL.style);
  doc.setFontSize(8.5);
  doc.text(
    coachEmail || "support@flexcellence.co",
    PAGE_LAYOUT.WIDTH / 2,
    y + 23,
    {
      align: "center",
    }
  );
}

// ==================== PROFILE PAGE ====================
async function addProfilePage(
  doc,
  userData,
  planData,
  coachName,
  coachEmail,
  nutritionGoals
) {
  let y = SPACING.PAGE_MARGIN;

  // Page Header
  drawSectionHeader(doc, "Client Profile & Nutrition Overview", y);
  y += 16;

  // Personal Information Section
  doc.setFillColor(...COLORS.LIGHT_GRAY);
  doc.roundedRect(SPACING.PAGE_MARGIN, y, 170, 65, 2, 2, "F");

  doc.setFont(FONTS.SUBHEADING.family, FONTS.SUBHEADING.style);
  doc.setFontSize(11);
  doc.setTextColor(...COLORS.SECONDARY);
  doc.text("Personal Information", SPACING.PAGE_MARGIN + 5, y + 8);

  y += 14;

  const leftCol = SPACING.PAGE_MARGIN + 5;
  const rightCol = 115;
  let leftY = y;
  let rightY = y;

  doc.setFont(FONTS.BODY.family, FONTS.BODY.style);
  doc.setFontSize(9.5);
  doc.setTextColor(...COLORS.TEXT_DARK);

  // Left Column - Personal Info
  const personalInfo = [
    {
      label: "Full Name:",
      value: `${userData.firstName} ${userData.lastName}`,
    },
    { label: "Age:", value: userData.age || "Not specified" },
    { label: "Gender:", value: capitalize(userData.gender) || "Not specified" },
    { label: "Email:", value: userData.email || "Not provided" },
  ];

  personalInfo.forEach((item) => {
    doc.setFont(FONTS.BODY.family, "bold");
    doc.text(item.label, leftCol, leftY);
    doc.setFont(FONTS.BODY.family, "normal");
    const valueText = doc.splitTextToSize(String(item.value), 45);
    doc.text(valueText[0], leftCol + 22, leftY);
    leftY += 6.5;
  });

  // Right Column - Body Metrics
  const height = userData.height
    ? `${userData.height} ${userData.height_units || "cm"}`
    : "Not specified";
  const weight = userData.weight
    ? `${userData.weight} ${userData.weight_units || "kg"}`
    : "Not specified";
  const bmi = calculateBMI(
    userData.weight,
    userData.height,
    userData.height_units,
    userData.weight_units
  );
  const bmiInfo = getBMIInfo(bmi);

  const bodyMetrics = [
    { label: "Height:", value: height },
    { label: "Weight:", value: weight },
    {
      label: "BMI:",
      value: bmi ? `${bmi} (${bmiInfo.label})` : "N/A",
      color: bmiInfo.color,
    },
  ];

  bodyMetrics.forEach((item) => {
    doc.setFont(FONTS.BODY.family, "bold");
    doc.setTextColor(...COLORS.TEXT_DARK);
    doc.text(item.label, rightCol, rightY);
    doc.setFont(FONTS.BODY.family, "normal");
    doc.setTextColor(...(item.color || COLORS.TEXT_DARK));
    doc.text(String(item.value), rightCol + 18, rightY);
    rightY += 6.5;
  });

  y = Math.max(leftY, rightY) + 6;

  // Nutrition Goals Section
  y += SPACING.SECTION_GAP;
  doc.setFillColor(...COLORS.LIGHT_GRAY);
  doc.roundedRect(SPACING.PAGE_MARGIN, y, 170, 75, 2, 2, "F");

  doc.setFont(FONTS.SUBHEADING.family, FONTS.SUBHEADING.style);
  doc.setFontSize(11);
  doc.setTextColor(...COLORS.SECONDARY);
  doc.text("Daily Nutrition Goals", SPACING.PAGE_MARGIN + 5, y + 8);

  y += 16;

  const cardWidth = 53;
  const cardHeight = 20;
  const gap = 4;
  const startX = SPACING.PAGE_MARGIN + 5;

  const safeValue = (val) => {
    const num = parseFloat(val);
    return isNaN(num) ? 0 : Math.round(num);
  };

  const nutritionCards = [
    {
      label: "Calories",
      value: `${safeValue(nutritionGoals?.ENERC_KCAL)} kcal`,
      color: COLORS.PRIMARY,
    },
    {
      label: "Protein",
      value: `${safeValue(nutritionGoals?.PROCNT)}g`,
      color: COLORS.SUCCESS,
    },
    {
      label: "Carbs",
      value: `${safeValue(nutritionGoals?.CHOCDF)}g`,
      color: COLORS.INFO,
    },
    {
      label: "Fats",
      value: `${safeValue(nutritionGoals?.FAT)}g`,
      color: COLORS.WARNING,
    },
    {
      label: "Fiber",
      value: `${safeValue(nutritionGoals?.FIBTG)}g`,
      color: COLORS.SUCCESS,
    },
    {
      label: "Sugar",
      value: `${safeValue(nutritionGoals?.SUGAR)}g`,
      color: COLORS.DANGER,
    },
  ];

  nutritionCards.forEach((card, index) => {
    const col = index % 3;
    const row = Math.floor(index / 3);
    const x = startX + col * (cardWidth + gap);
    const cardY = y + row * (cardHeight + gap);

    doc.setFillColor(...COLORS.WHITE);
    doc.setDrawColor(...COLORS.BORDER);
    doc.setLineWidth(0.25);
    doc.roundedRect(x, cardY, cardWidth, cardHeight, 1.5, 1.5, "FD");

    doc.setFont(FONTS.SMALL.family, FONTS.SMALL.style);
    doc.setFontSize(8.5);
    doc.setTextColor(...COLORS.TEXT_LIGHT);
    doc.text(card.label, x + cardWidth / 2, cardY + 7, { align: "center" });

    doc.setFont(FONTS.SUBHEADING.family, FONTS.SUBHEADING.style);
    doc.setFontSize(12);
    doc.setTextColor(...card.color);
    doc.text(card.value, x + cardWidth / 2, cardY + 15, { align: "center" });
  });

  y += 60;

  // Contact Section
  doc.setFillColor(...COLORS.PRIMARY);
  doc.roundedRect(SPACING.PAGE_MARGIN, y, 170, 32, 2, 2, "F");

  doc.setFont(FONTS.SUBHEADING.family, FONTS.SUBHEADING.style);
  doc.setFontSize(10.5);
  doc.setTextColor(...COLORS.WHITE);
  doc.text("Need Support?", SPACING.PAGE_MARGIN + 5, y + 8);

  doc.setFont(FONTS.SMALL.family, FONTS.SMALL.style);
  doc.setFontSize(8.5);
  doc.text(`Coach: ${coachName}`, SPACING.PAGE_MARGIN + 5, y + 15);
  doc.text(
    `Email: ${coachEmail || "support@flexcellence.co"}`,
    SPACING.PAGE_MARGIN + 5,
    y + 21
  );
  doc.text("Website: https://flexcellence.co", SPACING.PAGE_MARGIN + 5, y + 27);
  doc.text("Instagram: @FLEXCELLENCE.co", 115, y + 27);
}

// ==================== MEAL PLAN PAGES ====================
function addAllMealPlans(doc, planData, _userData) {
  let y = SPACING.PAGE_MARGIN;

  const mealTypeKeys = Object.keys(planData.meal_type_totals || {});
  const allMealTypes = sortMealTypesForPDF(mealTypeKeys);

  allMealTypes.forEach((mealType, mealIndex) => {
    const mealData = planData.meal_type_totals?.[mealType];
    if (!mealData) return;

    // Add spacing between meal types
    if (mealIndex > 0) {
      y += SPACING.SECTION_GAP;
    }

    if (y > 240) {
      doc.addPage();
      y = SPACING.PAGE_MARGIN;
    }

    drawSectionHeader(doc, `${mealType} Options`, y);
    y += 16;

    const options = [];
    const maxOptions = 7;
    const dayLabels = [
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
      "Sunday",
    ];
    const dayLabelsShort = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

    for (let i = 1; i <= maxOptions; i++) {
      const optionKey = `Option ${i}`;
      const optionData = mealData[optionKey];

      if (optionData && optionData.foods && optionData.foods.length > 0) {
        const label = dayLabels[options.length] || optionKey;
        const shortLabel = dayLabelsShort[options.length] || optionKey;
        options.push({
          label,
          shortLabel,
          foods: optionData.foods,
          nutrition: {
            calories: optionData.totalCalories || 0,
            protein: optionData.totalProtein || 0,
            carbs: optionData.totalCarbs || 0,
            fats: optionData.totalFats || 0,
            fiber: optionData.totalFiber || 0,
            sugar: optionData.totalSugar || 0,
          },
        });
      }
    }

    if (options.length === 0) {
      doc.setFont(FONTS.BODY.family, "italic");
      doc.setFontSize(9.5);
      doc.setTextColor(...COLORS.TEXT_LIGHT);
      doc.text(
        "No meal options available for this category.",
        PAGE_LAYOUT.WIDTH / 2,
        y + 20,
        {
          align: "center",
        }
      );
      y += 30;
      return;
    }

    const lineHeight = 5;
    const topPadding = 10;
    const nutritionHeight = 7;
    const bottomPadding = 3;
    const maxFoodWidth = 150;

    options.forEach((option, _optionIndex) => {
      const foods = option.foods || [];
      let contentLines = 0;
      foods.forEach((food) => {
        const foodText = `${food.name || "Unknown"} - ${food.quantity ?? ""}${food.unit ? " " + food.unit : ""}`;
        contentLines += doc.splitTextToSize(foodText, maxFoodWidth).length;
      });
      const cardHeight =
        topPadding +
        contentLines * lineHeight +
        nutritionHeight +
        bottomPadding;

      if (y + cardHeight > PAGE_LAYOUT.HEIGHT - 30) {
        doc.addPage();
        y = SPACING.PAGE_MARGIN;
        drawSectionHeader(doc, `${mealType} Options (continued)`, y);
        y += 16;
      }

      // Meal Card
      doc.setFillColor(...COLORS.LIGHT_GRAY);
      doc.setDrawColor(...COLORS.BORDER);
      doc.setLineWidth(0.2);
      doc.roundedRect(SPACING.PAGE_MARGIN, y, 170, cardHeight, 1.5, 1.5, "FD");

      // Day Badge
      const badgeText = option.shortLabel || option.label;
      const badgeWidth = Math.max(15, doc.getTextWidth(badgeText) + 10);

      doc.setFillColor(...COLORS.PRIMARY);
      doc.roundedRect(
        SPACING.PAGE_MARGIN + 3,
        y + 3,
        badgeWidth,
        7,
        1.5,
        1.5,
        "F"
      );
      doc.setTextColor(...COLORS.WHITE);
      doc.setFont(FONTS.BODY.family, "bold");
      doc.setFontSize(8.5);
      doc.text(badgeText, SPACING.PAGE_MARGIN + 3 + badgeWidth / 2, y + 7.5, {
        align: "center",
      });

      // Food Items
      doc.setTextColor(...COLORS.TEXT_DARK);
      doc.setFont(FONTS.BODY.family, "normal");
      doc.setFontSize(9.5);

      const contentX = SPACING.PAGE_MARGIN + 8;
      let foodY = y + 14;

      foods.forEach((food) => {
        const foodText = `${food.name || "Unknown"} - ${food.quantity ?? ""}${food.unit ? " " + food.unit : ""}`;

        doc.setFont(FONTS.BODY.family, "bold");
        doc.text("•", contentX, foodY);
        doc.setFont(FONTS.BODY.family, "normal");

        const wrappedText = doc.splitTextToSize(foodText, maxFoodWidth);
        wrappedText.forEach((line) => {
          doc.text(line, contentX + 4, foodY);
          foodY += lineHeight;
        });
      });

      // Nutrition Info
      const n = option.nutrition;
      const format = (val) =>
        typeof val === "number" && !isNaN(val) ? val.toFixed(1) : "0";

      const nutritionY = y + cardHeight - 4;
      doc.setFont(FONTS.SMALL.family, FONTS.SMALL.style);
      doc.setFontSize(7.5);
      doc.setTextColor(...COLORS.TEXT_LIGHT);

      const nutritionText = `${format(n.calories)} kcal  |  Protein: ${format(
        n.protein
      )}g  |  Carbs: ${format(n.carbs)}g  |  Fats: ${format(
        n.fats
      )}g  |  Fiber: ${format(n.fiber)}g`;
      doc.text(nutritionText, PAGE_LAYOUT.WIDTH / 2, nutritionY, {
        align: "center",
      });

      y += cardHeight + 5;
    });

    y += 4;
  });
}

// ==================== UTILITY FUNCTIONS ====================
function drawSectionHeader(doc, title, y) {
  doc.setFillColor(...COLORS.PRIMARY);
  doc.roundedRect(SPACING.PAGE_MARGIN, y, 170, 11, 2, 2, "F");

  doc.setTextColor(...COLORS.WHITE);
  doc.setFont(FONTS.HEADING.family, FONTS.HEADING.style);
  doc.setFontSize(13);
  doc.text(title, PAGE_LAYOUT.WIDTH / 2, y + 7.5, { align: "center" });
}

function calculateBMI(weight, height, heightUnits = "cm", weightUnits = "kg") {
  let h = Number(height);
  let w = Number(weight);
  if (!h || !w || isNaN(h) || isNaN(w) || h === 0) return null;

  if (heightUnits === "cm") h = h / 100;
  else if (heightUnits === "mm") h = h / 1000;
  else if (heightUnits === "inches") h = h * 0.0254;

  if (weightUnits === "g") w = w / 1000;
  else if (weightUnits === "lbs" || weightUnits === "pounds") w = w * 0.453592;

  return (w / (h * h)).toFixed(1);
}

function getBMIInfo(bmi) {
  if (!bmi) return { color: COLORS.TEXT_DARK, label: "N/A" };

  if (bmi < 18.5) return { color: COLORS.INFO, label: "Underweight" };
  if (bmi < 25) return { color: COLORS.SUCCESS, label: "Normal" };
  if (bmi < 30) return { color: COLORS.WARNING, label: "Overweight" };
  if (bmi < 35) return { color: COLORS.DANGER, label: "Obesity I" };
  if (bmi < 40) return { color: [200, 0, 0], label: "Obesity II" };
  return { color: [120, 0, 0], label: "Obesity III" };
}

function capitalize(str) {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
}
